import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from '../../_css/Pricing.module.css';
import { usePricing } from './PricingContext';
import {
  buildAdjustmentPayload,
  buildCreateContractPayload,
  buildOverrideContractPayload,
  buildToggleRenewalPayload,
  CYCLE_SELECT_OPTIONS,
  effectiveDisplayToIso,
  formatCycleLabel,
  mapContractAdjustmentsFromApi,
  mapContractFromApi,
  mapContractsFromApi,
  TIER_SELECT_OPTIONS,
  unwrapApiData,
} from './pricingTransforms';
import {
  createPricingContract,
  deletePricingContract,
  getAllAgencies,
  getPricingChangeHistory,
  getPricingContracts,
  makePricingAdjustments,
  overridePricingContract,
  restartContractAfterPoBlock,
  startNewContractAfterCancel,
  toggleContractRenewal,
} from '../../requests';
import { logoutOnUnauthorized, notifyError, notifySuccess } from '../../utils/helper';
import { validateEmail } from '../helper/validators';
import { formatTermDay } from '../helper/formatters';
import GeneratePersonaConfirmModal from '../Modals/GeneratePersonaConfirmModal';
import DeletePopUp from '../Modals/DeletePopUp';
import {
  PageHead,
  StatusPill,
  TierPill,
  CreditInput,
  Panel,
  ActionsBar,
  Select,
  Btn,
  DateInput,
} from './shared';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const emptyPagination = {
  total: 0,
  totalPages: 1,
  currentPage: 1,
  perPage: DEFAULT_LIMIT,
  hasNextPage: false,
  hasPrevPage: false,
};

function AdjustmentModal({ open, type, onClose, onSubmit, submitting }) {
  const [credits, setCredits] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setCredits('');
      setReason('');
    }
  }, [open, type]);

  if (!open) return null;

  const title =
    type === 'top-up' ? 'Top-up order' : type === 'adjustment' ? 'Adjustment' : 'Grant credits';

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="presentation">
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="adjustment-modal-title"
      >
        <div className={styles.modalHead}>
          <h3 id="adjustment-modal-title">{title}</h3>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Credits</div>
            <CreditInput value={credits} onChange={setCredits} suffix="" />
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Reason</div>
            <span className={styles.inp} style={{ flex: 1 }}>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional"
                disabled={submitting}
              />
            </span>
          </div>
        </div>
        <div className={styles.modalFoot}>
          <Btn ghost onClick={onClose} disabled={submitting}>
            Cancel
          </Btn>
          <Btn primary onClick={() => onSubmit({ credits, reason })} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function CloseRenewalModal({ client, onClose, onConfirm, submitting }) {
  if (!client) return null;

  const pausing = client.autoRenew;
  const title = pausing ? 'Stop auto-renewal' : 'Resume auto-renewal';
  const body = pausing
    ? `${client.name}'s contract stays active until its current end date, then it will not renew automatically.`
    : `${client.name}'s contract will renew automatically again at its next cycle boundary.`;

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="presentation">
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="renewal-modal-title"
      >
        <div className={styles.modalHead}>
          <h3 id="renewal-modal-title">{title}</h3>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          <p>{body}</p>
        </div>
        <div className={styles.modalFoot}>
          <Btn ghost onClick={onClose} disabled={submitting}>
            Cancel
          </Btn>
          <Btn primary onClick={onConfirm} disabled={submitting}>
            {submitting ? 'Saving…' : pausing ? 'Stop renewal' : 'Resume renewal'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function AddClientModal({ open, onClose, onSubmit, submitting, planTiers, trialPlan, unlimitedPlan }) {
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState('');
  const [tierId, setTierId] = useState('S');
  const [cycle, setCycle] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [capCredits, setCapCredits] = useState('');
  const [override, setOverride] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [billingEmailTouched, setBillingEmailTouched] = useState(false);

  // INV-14: prefill the billing address of the agency's current contract, so an
  // override does not start the new contract without it. Typed input wins.
  useEffect(() => {
    if (!open || !agencyId || billingEmailTouched) return;
    let cancelled = false;
    const firstRow = (res) => (Array.isArray(res?.data?.data) ? res.data.data[0] : null);
    // Active contract first; else the agency's last closed contract (e.g. after a trial, INV-15).
    getPricingContracts({ page: 1, limit: 1, agency_id: agencyId })
      .then(async (res) => {
        const current =
          firstRow(res) ??
          firstRow(await getPricingContracts({ page: 1, limit: 1, agency_id: agencyId, status: 'closed' }));
        if (!cancelled) setBillingEmail(current?.billing_email || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, agencyId, billingEmailTouched]);

  useEffect(() => {
    if (!open) return;

    setAgencyId('');
    setTierId('S');
    setCycle('monthly');
    setStartDate('');
    setCapCredits('');
    setOverride(false);
    setBillingEmail('');
    setBillingEmailTouched(false);

    getAllAgencies({ page: 1, limit: 100 })
      .then((res) => {
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setAgencies(
          rows.map((item) => ({
            id: item?.agency?.id ?? item?.id,
            name: item?.agency?.name ?? item?.name ?? `Agency ${item?.id}`,
          }))
        );
      })
      .catch(() => notifyError('Failed to load agencies'));
  }, [open]);

  if (!open) return null;

  const tierOptions = [
    ...TIER_SELECT_OPTIONS,
    ...(trialPlan ? [{ value: 'T', label: 'T (Trial)' }] : []),
    ...(unlimitedPlan ? [{ value: 'U', label: 'U (Unlimited)' }] : []),
  ];
  const selectedPlan =
    tierId === 'T' ? trialPlan
      : tierId === 'U' ? unlimitedPlan
        : planTiers.find((plan) => plan.id === tierId);

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="presentation">
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="add-client-modal-title"
      >
        <div className={styles.modalHead}>
          <h3 id="add-client-modal-title">Add client contract</h3>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Agency</div>
            <Select
              block
              value={agencyId}
              onChange={setAgencyId}
              options={[
                { value: '', label: 'Select agency' },
                ...agencies.map((agency) => ({
                  value: String(agency.id),
                  label: `MPSA-${agency.id} - ${agency.name}`,
                })),
              ]}
            />
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Tier / plan</div>
            <Select
              value={tierId}
              onChange={setTierId}
              options={tierOptions}
            />
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Billing cycle</div>
            {tierId === 'U' ? (
              <span className={styles.muted}>No billing cycle (unlimited)</span>
            ) : (
              <Select
                value={cycle}
                onChange={setCycle}
                options={CYCLE_SELECT_OPTIONS}
              />
            )}
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Start date</div>
            <DateInput value={startDate} onChange={setStartDate} />
          </div>
          {/* <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Cap credits</div>
            <CreditInput value={capCredits} onChange={setCapCredits} />
          </div> */}
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Billing email</div>
            <span className={styles.inp} style={{ flex: 1 }}>
              <input
                type="email"
                value={billingEmail}
                onChange={(e) => {
                  setBillingEmailTouched(true);
                  setBillingEmail(e.target.value);
                }}
                placeholder="billing@example.com"
                disabled={submitting}
              />
            </span>
          </div>
          <label className={styles.setRow} style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => setOverride(e.target.checked)}
              disabled={submitting}
            />
            <span className={styles.setRowLabel} style={{ marginLeft: 8 }}>
              Override existing contract for this agency
            </span>
          </label>
          {!selectedPlan?.planId && (
            <p className={styles.muted} style={{ fontSize: 12 }}>
              No active plan found for the selected tier.
            </p>
          )}
        </div>
        <div className={styles.modalFoot}>
          <Btn ghost onClick={onClose} disabled={submitting}>
            Cancel
          </Btn>
          <Btn
            primary
            onClick={() => {
              if (billingEmail.trim() && !validateEmail(billingEmail.trim())) {
                notifyError('Please enter a valid billing email address');
                return;
              }
              onSubmit({
                agencyId,
                planId: selectedPlan?.planId,
                cycle,
                startDate,
                capCredits,
                override,
                billingEmail: billingEmail.trim(),
              });
            }}
            disabled={submitting || !agencyId || !selectedPlan?.planId}
          >
            {submitting ? 'Creating…' : 'Create contract'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

const OVERRIDE_FIELD_LABELS = {
  tier: 'Tier',
  billingCycle: 'Billing cycle',
  includedCredits: 'Included credits',
  billingEmail: 'Billing email',
};

function describeOverrideValue(field, value) {
  if (field === 'tier') {
    return TIER_SELECT_OPTIONS.find((opt) => opt.value === value)?.label
      || (value === 'T' ? 'T (Trial)' : value === 'U' ? 'U (Unlimited)' : value);
  }
  if (field === 'billingCycle') {
    return CYCLE_SELECT_OPTIONS.find((opt) => opt.value === value)?.label || value;
  }
  if (field === 'billingEmail') {
    return value?.trim() ? value.trim() : '(none)';
  }
  return `${value} credits`;
}

const APPLY_LIST_PRICE_MESSAGE =
  'Moves this contract to the current list price for its tier and billing cycle. Included credits follow the list value.';

function ContractOverrides({ client, onOverride, onCreditsBlur, onBillingEmailBlur, onApplyListPrice, saving, trialPlan, unlimitedPlan }) {
  const [includedCredits, setIncludedCredits] = useState(client.overrides.includedCredits);
  const [billingEmail, setBillingEmail] = useState(client.overrides.billingEmail ?? '');

  useEffect(() => {
    setIncludedCredits(client.overrides.includedCredits);
  }, [client.overrides.includedCredits]);

  useEffect(() => {
    setBillingEmail(client.overrides.billingEmail ?? '');
  }, [client.overrides.billingEmail]);

  const tierOptions = [
    ...TIER_SELECT_OPTIONS,
    ...(trialPlan ? [{ value: 'T', label: 'T (Trial)' }] : []),
    ...(unlimitedPlan ? [{ value: 'U', label: 'U (Unlimited)' }] : []),
  ];
  const isUnlimited = client.overrides.tier === 'U';

  return (
    <div className={styles.mini}>
      <h4>Contract &amp; overrides</h4>
      <div className={styles.setRow}>
        <div className={styles.setRowLabel}>Tier</div>
        <Select
          value={client.overrides.tier}
          onChange={(value) => onOverride('tier', value)}
          options={tierOptions}
          disabled={saving}
        />
      </div>
      <div className={styles.setRow}>
        <div className={styles.setRowLabel}>
          Included credits {!isUnlimited && <span className={styles.overrideTag}>override</span>}
        </div>
        {isUnlimited ? (
          <span className={styles.muted}>Unlimited</span>
        ) : (
          <CreditInput
            value={includedCredits}
            onChange={setIncludedCredits}
            onBlur={() => {
              if (includedCredits !== client.overrides.includedCredits) {
                onCreditsBlur(includedCredits);
              }
            }}
          />
        )}
      </div>
      <div className={styles.setRow}>
        <div className={styles.setRowLabel}>Billing cycle</div>
        {client.overrides.tier === 'T' ? (
          <span className={styles.muted}>Trial (auto, 14 days)</span>
        ) : isUnlimited ? (
          <span className={styles.muted}>Unlimited (no expiry)</span>
        ) : (
          <Select
            value={client.overrides.billingCycle}
            onChange={(value) => onOverride('billingCycle', value)}
            options={CYCLE_SELECT_OPTIONS}
            disabled={saving}
          />
        )}
      </div>
      <div className={styles.setRow}>
        <div className={styles.setRowLabel}>
          Billing email <span className={styles.overrideTag}>override</span>
        </div>
        <span className={styles.inp} style={{ flex: 1 }}>
          <input
            type="email"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            onBlur={() => {
              const trimmed = billingEmail.trim();
              if (trimmed && !validateEmail(trimmed)) {
                notifyError('Please enter a valid billing email address');
                setBillingEmail(client.overrides.billingEmail ?? '');
                return;
              }
              // INV-14: a blank field keeps the stored address (the backend does
              // the same), so show it again instead of sending an empty value.
              if (!trimmed) {
                setBillingEmail(client.overrides.billingEmail ?? '');
                return;
              }
              if (trimmed !== (client.overrides.billingEmail ?? '')) {
                onBillingEmailBlur(trimmed);
              }
            }}
            placeholder="billing@example.com"
            disabled={saving}
          />
        </span>
      </div>
      {client.startDate && client.endDate && (
        <div className={styles.setRow}>
          <div className={styles.setRowLabel}>Term</div>
          {/* #30 / INV-23: the term ends on its last usable day (end_date is the
              renewal day), like the invoice mail. Dates are UTC days. */}
          <span className={styles.muted}>
            {formatTermDay(client.startDate)} →{' '}
            {client.tier === 'U' ? 'No expiry' : formatTermDay(client.endDate, { lastUsableDay: true })}
          </span>
        </div>
      )}
      {!['T', 'U'].includes(client.overrides.tier) && (
        <ActionsBar>
          <Btn ghost sm onClick={onApplyListPrice} disabled={saving}>
            Apply current list price
          </Btn>
        </ActionsBar>
      )}
    </div>
  );
}

function GrantsPanel({ grants, loading, onOpenAdjustment }) {
  return (
    <div className={styles.mini}>
      <h4>Credit grants, top-ups &amp; adjustments</h4>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Type</th>
            <th className={styles.alignRight}>Credits</th>
            <th>Reason</th>
            <th className={styles.alignRight}>By</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={4} className={styles.muted}>
                Loading history…
              </td>
            </tr>
          ) : grants.length === 0 ? (
            <tr>
              <td colSpan={4} className={styles.muted}>
                No grants yet
              </td>
            </tr>
          ) : (
            grants.map((grant) => (
              <tr key={grant.id}>
                <td>
                  <StatusPill status={grant.type} />
                </td>
                <td className={styles.alignRight}>{grant.credits}</td>
                <td className={styles.muted}>{grant.reason}</td>
                <td className={`${styles.alignRight} ${styles.muted}`}>{grant.by}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <ActionsBar>
        <Btn ghost sm onClick={() => onOpenAdjustment('grant')}>
          + Grant
        </Btn>
        <Btn ghost sm onClick={() => onOpenAdjustment('top-up')}>
          + Top-up order
        </Btn>
        <Btn ghost sm onClick={() => onOpenAdjustment('adjustment')}>
          + Adjustment
        </Btn>
      </ActionsBar>
    </div>
  );
}

function ClientDetail({ client, onContractUpdated, onReloadContracts, onDeleteContract, trialPlan, unlimitedPlan }) {
  const [localClient, setLocalClient] = useState(client);
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(true);
  const [savingOverride, setSavingOverride] = useState(false);
  const [pendingOverride, setPendingOverride] = useState(null);
  const [overridesResetKey, setOverridesResetKey] = useState(0);
  const [adjustmentType, setAdjustmentType] = useState(null);
  const [submittingAdjustment, setSubmittingAdjustment] = useState(false);

  useEffect(() => {
    setLocalClient(client);
  }, [client]);

  const loadGrants = useCallback(async () => {
    try {
      setGrantsLoading(true);
      const res = await getPricingChangeHistory({
        page: 1,
        limit: 10,
        contract_id: client.id,
      });
      const payload = res?.data || {};
      const rows = Array.isArray(payload.data) ? payload.data : [];
      setGrants(mapContractAdjustmentsFromApi(rows));
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to load change history');
      setGrants([]);
    } finally {
      setGrantsLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const updateOverride = async (field, value) => {
    const nextOverrides = { ...localClient.overrides, [field]: value };
    setLocalClient((prev) => ({ ...prev, overrides: nextOverrides }));

    try {
      setSavingOverride(true);
      const payload = buildOverrideContractPayload(client.id, field, value);
      const res = await overridePricingContract(payload);
      const updated = mapContractFromApi(unwrapApiData(res));
      setLocalClient(updated);
      onContractUpdated(updated);
      await onReloadContracts();
      notifySuccess('Contract updated');
    } catch (err) {
      setLocalClient(client);
      notifyError(err?.response?.data?.message || 'Failed to update contract');
    } finally {
      setSavingOverride(false);
    }
  };

  const requestOverride = (field, value) => {
    if (value === localClient.overrides[field]) return;
    setPendingOverride({ field, value });
  };

  const cancelOverride = () => {
    setPendingOverride(null);
    setOverridesResetKey((key) => key + 1);
  };

  // Moves the contract to the current list-price row for its tier and cycle. The
  // server answers with what it did ("moved" or "already on the current list price").
  const applyListPrice = async () => {
    try {
      setSavingOverride(true);
      const res = await overridePricingContract(
        buildOverrideContractPayload(client.id, 'useListPrice', true)
      );
      await onReloadContracts();
      notifySuccess(res?.data?.message || 'Contract updated');
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to update contract');
    } finally {
      setSavingOverride(false);
    }
  };

  const confirmOverride = async () => {
    if (!pendingOverride) return;
    const { field, value } = pendingOverride;
    setPendingOverride(null);
    if (field === 'useListPrice') {
      await applyListPrice();
      return;
    }
    await updateOverride(field, value);
  };

  const submitAdjustment = async ({ credits, reason }) => {
    if (!credits) {
      notifyError('Credits are required');
      return;
    }

    try {
      setSubmittingAdjustment(true);
      const payload = buildAdjustmentPayload({
        contractId: client.id,
        type: adjustmentType,
        credits,
        reason,
      });
      const res = await makePricingAdjustments(payload);
      const updated = mapContractFromApi(unwrapApiData(res));
      setLocalClient(updated);
      onContractUpdated(updated);
      await loadGrants();
      await onReloadContracts();
      setAdjustmentType(null);
      notifySuccess('Adjustment saved');
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to save adjustment');
    } finally {
      setSubmittingAdjustment(false);
    }
  };

  return (
    <>
      <div className={styles.grid2}>
        <ContractOverrides
          key={overridesResetKey}
          client={localClient}
          onOverride={requestOverride}
          onCreditsBlur={(value) => requestOverride('includedCredits', value)}
          onBillingEmailBlur={(value) => requestOverride('billingEmail', value)}
          onApplyListPrice={() => setPendingOverride({ field: 'useListPrice', value: true })}
          saving={savingOverride}
          trialPlan={trialPlan}
          unlimitedPlan={unlimitedPlan}
        />
        <GrantsPanel
          grants={grants}
          loading={grantsLoading}
          onOpenAdjustment={setAdjustmentType}
        />
      </div>
      <ActionsBar>
        <Btn ghost sm onClick={() => onDeleteContract?.(client)}>
          Delete contract
        </Btn>
      </ActionsBar>
      <AdjustmentModal
        open={Boolean(adjustmentType)}
        type={adjustmentType}
        onClose={() => setAdjustmentType(null)}
        onSubmit={submitAdjustment}
        submitting={submittingAdjustment}
      />
      <GeneratePersonaConfirmModal
        open={Boolean(pendingOverride)}
        onClose={cancelOverride}
        onConfirm={confirmOverride}
        disabled={savingOverride}
        title={pendingOverride?.field === 'useListPrice' ? 'Apply current list price?' : 'Update contract?'}
        message={
          pendingOverride?.field === 'useListPrice'
            ? APPLY_LIST_PRICE_MESSAGE
            : pendingOverride
              ? `${OVERRIDE_FIELD_LABELS[pendingOverride.field]} will be updated to ${describeOverrideValue(pendingOverride.field, pendingOverride.value)}.`
              : ''
        }
      />
    </>
  );
}

// INV-10: contracts whose period ended without a new one used to vanish here.
const CONTRACT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  // #2: the archive - deleted agencies and single deleted contracts
  { value: 'deleted', label: 'Deleted' },
];

const CLOSED_REASON_LABELS = {
  po_missing: 'Closed · no PO',
  trial_ended: 'Trial ended',
  cancelled: 'Cancelled',
  overridden: 'Overridden',
  agency_deleted: 'Agency deleted',
  contract_deleted: 'Contract deleted',
  po_restarted: 'Closed',
  renewed: 'Closed',
  fiscal_split: 'Closed',
};

const EMPTY_TEXT = {
  active: 'No contracts yet.',
  closed: 'No closed contracts.',
  deleted: 'Nothing deleted yet.',
};

export default function ClientsSection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { planTiers, trialPlan, unlimitedPlan } = usePricing();
  const [clients, setClients] = useState([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [creatingContract, setCreatingContract] = useState(false);
  const [renewalTarget, setRenewalTarget] = useState(null);
  const [togglingRenewal, setTogglingRenewal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restartTarget, setRestartTarget] = useState(null);
  const [restarting, setRestarting] = useState(false);
  // Row whose "Start a new period today" was clicked without a PO number on file.
  const [noPoErrorId, setNoPoErrorId] = useState(null);
  // Cancelled and ended: tier/cycle chosen per row, and the row being confirmed.
  const [newContractChoice, setNewContractChoice] = useState({});
  const [newContractTarget, setNewContractTarget] = useState(null);
  const [startingNewContract, setStartingNewContract] = useState(false);

  const loadContracts = useCallback(async (pageNum = page) => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await getPricingContracts({ page: pageNum, limit: DEFAULT_LIMIT, status: statusFilter });
      if (logoutOnUnauthorized({ response: res }, dispatch, navigate)) return;
      const payload = res?.data || {};
      const { contracts, pagination: paginationData } = mapContractsFromApi(payload);
      setClients(contracts);
      setPagination({
        total: paginationData?.total ?? 0,
        totalPages: paginationData?.totalPages ?? 1,
        currentPage: paginationData?.currentPage ?? pageNum,
        perPage: paginationData?.perPage ?? DEFAULT_LIMIT,
        hasNextPage: paginationData?.hasNextPage ?? false,
        hasPrevPage: paginationData?.hasPrevPage ?? false,
      });
    } catch (err) {
      if (logoutOnUnauthorized(err, dispatch, navigate)) return;
      const message = err?.response?.data?.message || 'Failed to load contracts';
      setLoadError(message);
      notifyError(message);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dispatch, navigate]);

  const changeStatusFilter = (value) => {
    setStatusFilter(value || 'active');
    setExpandedId(null);
    setPage(DEFAULT_PAGE);
  };

  useEffect(() => {
    loadContracts(page);
  }, [page, loadContracts]);

  const toggleRow = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // #2: ask the backend first. It refuses a contract with used credits or a
  // sent invoice (and says why); otherwise it lists what will not be invoiced,
  // which the confirm dialog shows.
  const requestDeleteContract = async (client) => {
    try {
      const res = await deletePricingContract(client.id, { dryRun: true });
      setDeleteTarget({ ...client, notInvoiced: res?.data?.not_invoiced ?? [] });
    } catch (err) {
      notifyError(err?.response?.data?.message || 'This contract cannot be deleted');
    }
  };

  // INV-12: the backend refuses a contract with usage or a sent invoice and says why.
  const handleConfirmDeleteContract = async () => {
    if (!deleteTarget) return;
    try {
      const res = await deletePricingContract(deleteTarget.id);
      notifySuccess(res?.data?.message || 'Contract deleted');
      setDeleteTarget(null);
      setExpandedId(null);
      await loadContracts(page);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to delete contract');
      setDeleteTarget(null);
    }
  };

  // F-05: same restart as the agency admin's "Start a new period today"; the
  // backend refuses a cancelled contract, a missing PO or a running period.
  const handleConfirmRestart = async () => {
    if (!restartTarget) return;
    try {
      setRestarting(true);
      const res = await restartContractAfterPoBlock(restartTarget.agencyId);
      notifySuccess(res?.data?.message || 'New period started');
      setRestartTarget(null);
      await loadContracts(page);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to start a new period');
    } finally {
      setRestarting(false);
    }
  };

  // The cancelled contract's tier and cycle as defaults (S / monthly if it had none).
  const newContractChoiceFor = (client) => {
    const chosen = newContractChoice[client.id] || {};
    const lastTier = ['S', 'M', 'L'].includes(client.tier) ? client.tier : 'S';
    const lastCycle = ['monthly', 'yearly'].includes(client.cycleValue) ? client.cycleValue : 'monthly';
    return { tier: chosen.tier || lastTier, cycle: chosen.cycle || lastCycle };
  };

  const setNewContractChoiceFor = (client, patch) => {
    setNewContractChoice((prev) => ({ ...prev, [client.id]: { ...newContractChoiceFor(client), ...patch } }));
  };

  // Same start as the agency admin's "Start a new contract" after a cancel; the
  // backend refuses a missing PO, a running contract or a contract not cancelled.
  const handleConfirmNewContract = async () => {
    if (!newContractTarget) return;
    try {
      setStartingNewContract(true);
      const { tier, cycle } = newContractChoiceFor(newContractTarget);
      const res = await startNewContractAfterCancel({ agencyId: newContractTarget.agencyId, tier, cycle });
      notifySuccess(res?.data?.message || 'New contract started');
      setNewContractTarget(null);
      await loadContracts(page);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to start a new contract');
    } finally {
      setStartingNewContract(false);
    }
  };

  const handleContractUpdated = (updated) => {
    setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  const handleConfirmToggleRenewal = async () => {
    if (!renewalTarget) return;
    const nextAutoRenew = !renewalTarget.autoRenew;

    try {
      setTogglingRenewal(true);
      const payload = buildToggleRenewalPayload(renewalTarget.id, nextAutoRenew);
      await toggleContractRenewal(payload);
      setClients((prev) =>
        prev.map((c) => (c.id === renewalTarget.id ? { ...c, autoRenew: nextAutoRenew } : c))
      );
      notifySuccess(nextAutoRenew ? 'Contract renewal resumed' : 'Contract renewal paused');
      setRenewalTarget(null);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to update contract');
    } finally {
      setTogglingRenewal(false);
    }
  };

  const handleCreateContract = async ({
    agencyId,
    planId,
    cycle,
    startDate,
    capCredits,
    override,
    billingEmail,
  }) => {
    if (!agencyId || !planId) {
      notifyError('Agency and plan are required');
      return;
    }

    const isoStartDate =
      effectiveDisplayToIso(startDate) || new Date().toISOString().slice(0, 10);

    try {
      setCreatingContract(true);
      const payload = buildCreateContractPayload({
        agencyId,
        planId,
        startDate: isoStartDate,
        capCredits,
        billingEmail,
        override,
        cycle,
      });
      const res = await createPricingContract(payload);
      const newContractId = unwrapApiData(res)?.contract?.id;
      // The backend creates the contract in the requested cycle (yearly row and
      // yearly contract mail). This override is then a no-op; it only matters
      // while an older backend, which ignores `cycle`, is still deployed.
      if (cycle === 'yearly' && newContractId) {
        await overridePricingContract(
          buildOverrideContractPayload(newContractId, 'billingCycle', 'yearly')
        );
      }
      setAddModalOpen(false);
      notifySuccess('Contract created');
      if (page !== DEFAULT_PAGE) {
        setPage(DEFAULT_PAGE);
      } else {
        await loadContracts(DEFAULT_PAGE);
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to create contract');
    } finally {
      setCreatingContract(false);
    }
  };

  if (loading && clients.length === 0) {
    return (
      <>
        <PageHead title="Clients & contracts" subtitle="Plans, overrides and credits per tenant" />
        <Panel>
          <p className={styles.muted}>Loading contracts…</p>
        </Panel>
      </>
    );
  }

  if (loadError && clients.length === 0) {
    return (
      <>
        <PageHead title="Clients & contracts" subtitle="Plans, overrides and credits per tenant" />
        <Panel>
          <p className={styles.muted}>{loadError}</p>
          <ActionsBar>
            <Btn primary sm onClick={() => loadContracts(page)}>
              Retry
            </Btn>
          </ActionsBar>
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHead title="Clients & contracts" subtitle="Plans, overrides and credits per tenant" />

      <Panel>
        <div className={styles.filterBar}>
          <span className={styles.filterLabel}>Show</span>
          <Select small value={statusFilter} onChange={changeStatusFilter} options={CONTRACT_STATUS_OPTIONS} />
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Agency ID</th>
              <th style={{ width: '28%' }}>Client / agency</th>
              <th>Tier</th>
              <th>Cycle</th>
              <th className={styles.alignRight}>Credits (used / incl.)</th>
              <th>Renewal</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.muted}>
                  {EMPTY_TEXT[statusFilter] || EMPTY_TEXT.active}
                </td>
              </tr>
            ) : (
              clients.map((client) => (
                <React.Fragment key={client.id}>
                  <tr
                    className={client.isActive ? styles.rowLink : undefined}
                    onClick={client.isActive ? () => toggleRow(client.id) : undefined}
                  >
                    <td className={styles.muted}>
                      {client.agencyId ? `MPSA-${client.agencyId}` : '—'}
                    </td>
                    <td>
                      <b>{client.name}</b>
                    </td>
                    <td>
                      {client.tier} <TierPill tierId={client.tier} />
                      {client.tierNote && (
                        <span className={styles.overrideTag}>{client.tierNote}</span>
                      )}
                    </td>
                    <td>
                      {client.cycle}
                      {client.cycleOverride && (
                        <span className={styles.overrideTag}>override</span>
                      )}
                    </td>
                    <td className={styles.alignRight}>
                      {client.creditsUsed} / {client.tier === 'U' ? 'Unlimited' : client.creditsIncluded}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {!client.isActive ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <StatusPill
                              status={
                                client.closedReason === 'contract_deleted'
                                  ? 'Contract deleted'
                                  : client.agencyDeleted
                                    ? 'Agency deleted'
                                    : CLOSED_REASON_LABELS[client.closedReason] || 'Closed'
                              }
                            />
                            {/* Archived rows (#2) cannot be deleted again. */}
                            {statusFilter !== 'deleted' && (
                              <Btn ghost sm onClick={() => requestDeleteContract(client)}>
                                Delete
                              </Btn>
                            )}
                          </div>
                          {/* Paused for a missing PO (not cancelled): a super admin can
                              start a new period once a PO number is on file. */}
                          {statusFilter === 'closed' &&
                            client.closedReason === 'po_missing' &&
                            client.autoRenew &&
                            !client.agencyDeleted && (
                              <>
                                <Btn
                                  primary
                                  sm
                                  onClick={() => {
                                    if (!client.poNumber.trim()) {
                                      setNoPoErrorId(client.id);
                                      return;
                                    }
                                    setNoPoErrorId(null);
                                    setRestartTarget(client);
                                  }}
                                  disabled={restarting}
                                >
                                  Start a new period today
                                </Btn>
                                {noPoErrorId === client.id && !client.poNumber.trim() && (
                                  <span className={styles.inlineError}>
                                    No PO number on file. Save the agency's PO number first.
                                  </span>
                                )}
                              </>
                            )}
                          {/* Cancelled and ended: a super admin can start a new contract
                              (same as the agency admin's Cost Center) once a PO number is on file. */}
                          {statusFilter === 'closed' &&
                            client.closedReason === 'cancelled' &&
                            !client.agencyDeleted && (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <Select
                                    value={newContractChoiceFor(client).tier}
                                    onChange={(tier) => setNewContractChoiceFor(client, { tier })}
                                    options={TIER_SELECT_OPTIONS}
                                  />
                                  <Select
                                    value={newContractChoiceFor(client).cycle}
                                    onChange={(cycle) => setNewContractChoiceFor(client, { cycle })}
                                    options={CYCLE_SELECT_OPTIONS}
                                  />
                                </div>
                                <Btn
                                  primary
                                  sm
                                  onClick={() => {
                                    if (!client.poNumber.trim()) {
                                      setNoPoErrorId(client.id);
                                      return;
                                    }
                                    setNoPoErrorId(null);
                                    setNewContractTarget(client);
                                  }}
                                  disabled={startingNewContract}
                                >
                                  Start a new contract
                                </Btn>
                                {noPoErrorId === client.id && !client.poNumber.trim() && (
                                  <span className={styles.inlineError}>
                                    No PO number on file. Save the agency's PO number first.
                                  </span>
                                )}
                              </>
                            )}
                        </div>
                      ) : client.cycleValue === 'trial' ? (
                        <StatusPill status="Trial" />
                      ) : client.cycleValue === 'unlimited' ? (
                        <StatusPill status="Unlimited" />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <StatusPill status={client.autoRenew ? 'Active' : 'Paused'} />
                          <Btn ghost sm onClick={() => setRenewalTarget(client)}>
                            {client.autoRenew ? 'Close' : 'Resume'}
                          </Btn>
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedId === client.id && (
                    <tr className={styles.detailRow}>
                      <td colSpan={6}>
                        <ClientDetail
                          client={client}
                          onContractUpdated={handleContractUpdated}
                          onReloadContracts={() => loadContracts(page)}
                          onDeleteContract={requestDeleteContract}
                          trialPlan={trialPlan}
                          unlimitedPlan={unlimitedPlan}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>

        {pagination.totalPages > 1 && (
          <div className={styles.actions} style={{ justifyContent: 'space-between' }}>
            <span className={styles.muted} style={{ fontSize: 12 }}>
              Page {pagination.currentPage} of {pagination.totalPages} · {pagination.total}{' '}
              {statusFilter === 'active' ? 'contracts' : 'agencies'}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn
                ghost
                sm
                disabled={!pagination.hasPrevPage || loading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Btn>
              <Btn
                ghost
                sm
                disabled={!pagination.hasNextPage || loading}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next
              </Btn>
            </div>
          </div>
        )}

        <ActionsBar>
          <Btn ghost sm onClick={() => setAddModalOpen(true)}>
            + Add client
          </Btn>
        </ActionsBar>
      </Panel>

      <AddClientModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={handleCreateContract}
        submitting={creatingContract}
        planTiers={planTiers}
        trialPlan={trialPlan}
        unlimitedPlan={unlimitedPlan}
      />

      {deleteTarget && (
        <DeletePopUp
          variant="delete"
          title="Delete contract"
          message={`Delete contract ${deleteTarget.id} of "${deleteTarget.name}"? It then no longer counts anywhere and moves to the archive (Show: Deleted).${
            deleteTarget.notInvoiced?.length ? ` ${deleteTarget.notInvoiced.join('. ')}.` : ''
          } This cannot be undone.`}
          onClose={() => setDeleteTarget(null)}
          onDelete={handleConfirmDeleteContract}
        />
      )}

      <GeneratePersonaConfirmModal
        open={Boolean(restartTarget)}
        onClose={() => setRestartTarget(null)}
        onConfirm={handleConfirmRestart}
        disabled={restarting}
        title="Start a new period today?"
        message={
          restartTarget
            ? `A new ${formatCycleLabel(restartTarget.cycleValue || 'monthly').toLowerCase()} period for "${restartTarget.name}" starts today, ${formatTermDay(new Date().toISOString())}${
                String(restartTarget.cycleValue || '').toLowerCase() === 'yearly' ? ', and runs 12 months' : ''
              }. Days without service are not charged.`
            : ''
        }
      />

      <GeneratePersonaConfirmModal
        open={Boolean(newContractTarget)}
        onClose={() => setNewContractTarget(null)}
        onConfirm={handleConfirmNewContract}
        disabled={startingNewContract}
        title="Start a new contract?"
        message={
          newContractTarget
            ? (() => {
                const { tier, cycle } = newContractChoiceFor(newContractTarget);
                const tierLabel = TIER_SELECT_OPTIONS.find((o) => o.value === tier)?.label ?? tier;
                return `A new ${formatCycleLabel(cycle).toLowerCase()} ${tierLabel} contract for "${newContractTarget.name}" starts today, ${formatTermDay(new Date().toISOString())}${
                  cycle === 'yearly' ? ', and runs 12 months' : ''
                }, at the current list price. Unused top-up credits of the cancelled contract carry over.`;
              })()
            : ''
        }
      />

      <CloseRenewalModal
        client={renewalTarget}
        onClose={() => setRenewalTarget(null)}
        onConfirm={handleConfirmToggleRenewal}
        submitting={togglingRenewal}
      />
    </>
  );
}
